// frontend/src/pages/NameMatchVerification.tsx
//
// Auto-fire component: on mount, sequentially calls a configurable subset of
// Caprion name-match / identity verification endpoints, then calls onDone() to
// advance the journey. Shows a spinner while running.
//
// FAILURE BEHAVIOUR (Aadhaar identity checks):
//   If ANY verification returns status="failed", the component:
//     1. Shows a toast for ~2.5 s ("Aadhaar verification failed")
//     2. Calls POST /api/caprion/verify/lock-aadhaar-verification/ to persist the
//        block in LoanJourneyState.meta — survives refresh and re-login
//     3. Greys out the page and shows a permanent error card with a Contact Support
//        button (CallbackHelp dialog). The Continue button is hidden/disabled.
//   The user CANNOT advance until an admin clears meta.aadhaar_verification_blocked.
//
// ON MOUNT (re-login / refresh):
//   Before running verifications the component fetches the journey state and checks
//   meta.aadhaar_verification_blocked. If true, it immediately shows the blocked UI
//   without re-running verifications.
//
// AADHAAR_IDENTITY_ENDPOINTS (4 checks — Page A, right after Aadhaar/selfie page):
//   1. Individual vs Aadhaar name       → POST /api/caprion/verify/aadhaar-name/
//   2. Individual vs Aadhaar gender     → POST /api/caprion/verify/aadhaar-gender/
//   3. Individual vs Aadhaar DOB        → POST /api/caprion/verify/aadhaar-dob/
//   4. PAN Details Plus vs Aadhaar last-2-digit → POST /api/caprion/verify/aadhaar-last4/
//
// NOTE: DigiLocker PAN name check (Individual vs PAN name via /api/caprion/verify/pan-name/)
// has been removed. Name verification uses PAN Details Plus only (pan-details-plus-name gate).
// NOTE: PAN DOB (/api/caprion/verify/pan-dob/) and PAN gender (/api/caprion/verify/pan-gender/)
// checks have been removed from identity verification.
//
// BANK_FINAL_ENDPOINTS (Page B, after eMandate Setup):
//   - Bank account name match           → POST /api/caprion/verify/bank-account-name/
//   - Final cross-checks                → POST /api/trigger_ruleset_b/

import React, { useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import IntellendLogo from "@/components/IntellendLogo";
import CallbackHelp from "@/components/CallbackHelp";
import { AlertTriangle, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";

// The full ordered set of endpoints (identity checks + bank + ruleset B cross-checks)
// NOTE: DigiLocker PAN name check (/api/caprion/verify/pan-name/) removed — not_applicable.
// NOTE: PAN DOB (/api/caprion/verify/pan-dob/) and PAN gender (/api/caprion/verify/pan-gender/) removed.
const ALL_VERIFICATION_ENDPOINTS = [
  { label: "Aadhaar name",          url: "/api/caprion/verify/aadhaar-name/" },
  { label: "Aadhaar gender",        url: "/api/caprion/verify/aadhaar-gender/" },
  { label: "Aadhaar DOB",           url: "/api/caprion/verify/aadhaar-dob/" },
  { label: "Aadhaar last-2 check",  url: "/api/caprion/verify/aadhaar-last4/" },
  { label: "Bank account name",     url: "/api/caprion/verify/bank-account-name/" },
  { label: "Final cross-checks",    url: "/api/trigger_ruleset_b/" },
];

// Subset for Page A: Aadhaar & Identity Verification (right after Aadhaar/selfie page)
// Fires exactly 4 checks — permanent block if any fail:
//   1. Individual vs Aadhaar name
//   2. Individual vs Aadhaar gender
//   3. Individual vs Aadhaar DOB
//   4. PAN Details Plus vs Aadhaar last-2-digit check
// NOTE: DigiLocker PAN name check (Individual vs PAN name) removed.
// NOTE: PAN DOB and PAN gender checks removed.
export const AADHAAR_IDENTITY_ENDPOINTS = [
  { label: "Aadhaar name",          url: "/api/caprion/verify/aadhaar-name/" },
  { label: "Aadhaar gender",        url: "/api/caprion/verify/aadhaar-gender/" },
  { label: "Aadhaar DOB",           url: "/api/caprion/verify/aadhaar-dob/" },
  { label: "Aadhaar last-2 check",  url: "/api/caprion/verify/aadhaar-last4/" },
];

// Subset for Page B: Bank & Final Verification (after eMandate Setup)
// Fires: Bank account name match + trigger_ruleset_b (final cross-checks)
export const BANK_FINAL_ENDPOINTS = ALL_VERIFICATION_ENDPOINTS.filter(
  ep => ep.url === "/api/caprion/verify/bank-account-name/" || ep.url === "/api/trigger_ruleset_b/"
);

// Legacy: all (kept for backward compatibility with old single-page setup)
const VERIFICATION_ENDPOINTS = ALL_VERIFICATION_ENDPOINTS;

interface NameMatchVerificationProps {
  onDone?: () => void;
  onAdvance?: () => void;
  isBuilderMode?: boolean;
  /** Which verification this instance represents (for builder palette label) */
  verificationLabel?: string;
  /**
   * Optional subset of endpoints to fire. Defaults to ALL_VERIFICATION_ENDPOINTS.
   * Use AADHAAR_IDENTITY_ENDPOINTS or BANK_FINAL_ENDPOINTS for split-page usage.
   */
  endpoints?: Array<{ label: string; url: string }>;
  /**
   * Extra POST body sent to each endpoint. Used to pass `no_advance: true` to
   * /api/trigger_ruleset_b/, which otherwise mutates the journey stage itself
   * (advancing on pass, setting REJECTED on fail) and fights this component's
   * own block-vs-advance decision.
   */
  requestBody?: Record<string, unknown>;
  /**
   * Backend endpoint to call when any verification fails (persists block in meta).
   * Defaults to the Aadhaar lock endpoint.
   * LOS_BankFinalVerification_V31 passes the bank-specific endpoint so the two
   * pages use separate meta keys and show the correct failure copy.
   */
  lockEndpoint?: string;
  /**
   * Optional NON-BLOCKING endpoint fired once all gates have passed, just
   * before the journey advances. Used for side-effect writes that must never
   * gate the applicant — currently the address-score/CPV snapshot
   * (/api/caprion/address-score-cpv/save/).
   *
   * Deliberately NOT part of `endpoints`: everything in that list is a GATE
   * whose failure blocks the user permanently. A failure here is swallowed.
   */
  postRunEndpoint?: string;
  /**
   * LoanJourneyState.meta key to check on mount for a pre-existing block.
   * Defaults to "aadhaar_verification_blocked".
   */
  blockedMetaKey?: string;
  /**
   * LoanJourneyState.meta key that holds the list of failed check labels.
   * Defaults to "aadhaar_verification_failed_checks".
   */
  failedChecksMetaKey?: string;
  /** Heading shown in the permanent blocked card. Defaults to "Aadhaar Verification Failed". */
  blockedTitle?: string;
  /** Body copy shown in the permanent blocked card. */
  blockedDescription?: string;
}

// ── Blocked UI ────────────────────────────────────────────────────────────────
interface BlockedViewProps {
  failedChecks?: string[];
  showToast: boolean;
  title?: string;
  description?: string;
}

const BlockedView: React.FC<BlockedViewProps> = ({ failedChecks, showToast, title, description }) => {
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <>
      {/* Full-page grey overlay */}
      <div className="fixed inset-0 bg-gray-100/80 backdrop-blur-sm z-10 pointer-events-none" />

      {/* Toast — visible for ~2.5 s via CSS animation, then fades out */}
      {showToast && (
        <div
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3
                     bg-red-600 text-white px-5 py-3 rounded-xl shadow-lg
                     animate-[fadeInOut_2.5s_ease-in-out_forwards]"
          style={{ animationFillMode: "forwards" }}
        >
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span className="text-sm font-semibold">Aadhaar verification failed</span>
        </div>
      )}

      {/* Blocked card — centred, above overlay */}
      <div className="relative z-20 min-h-screen flex flex-col justify-center items-center p-4">
        <div className="bg-white border border-red-200 rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 rounded-full p-4">
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {title ?? "Aadhaar Verification Failed"}
          </h2>

          {/* Body */}
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">
            {description ?? "We could not verify your Aadhaar details. Your application has been paused. Please contact our support team for assistance."}
          </p>

          {/* Failed checks list */}
          {failedChecks && failedChecks.length > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-5 text-left">
              <p className="text-xs font-semibold text-red-700 mb-1">Failed checks:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {failedChecks.map(c => (
                  <li key={c} className="text-xs text-red-600">{c}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact Support button */}
          <Button
            onClick={() => setSupportOpen(true)}
            className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white flex items-center justify-center gap-2"
          >
            <PhoneCall className="w-4 h-4" />
            Contact Support
          </Button>

          <p className="text-xs text-gray-400 mt-4">
            This page will remain blocked until our team resolves your case.
          </p>
        </div>
      </div>

      {/* CallbackHelp dialog */}
      <CallbackHelp
        isOpen={supportOpen}
        setIsOpen={setSupportOpen}
        title="Contact Support"
        description="Our team will help resolve your Aadhaar verification issue. We'll reach out to you shortly."
        showOk={true}
        showWhatsappUs={true}
        showCallUs={true}
        silentlyTrigger={true}
      />
    </>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const NameMatchVerification = ({
  onDone,
  onAdvance,
  isBuilderMode = false,
  verificationLabel = "Name Match Verification",
  endpoints,
  requestBody,
  postRunEndpoint,
  lockEndpoint = "/api/caprion/verify/lock-aadhaar-verification/",
  blockedMetaKey = "aadhaar_verification_blocked",
  failedChecksMetaKey = "aadhaar_verification_failed_checks",
  blockedTitle,
  blockedDescription,
}: NameMatchVerificationProps) => {
  // Use the provided subset or fall back to the full list
  const activeEndpoints = endpoints ?? VERIFICATION_ENDPOINTS;

  const [currentStep, setCurrentStep] = useState(0);
  const [retryIn, setRetryIn] = useState(0);
  const [isPending, setIsPending] = useState(false);

  // Blocked state — set when any verification fails; persisted in backend meta
  const [isBlocked, setIsBlocked] = useState(false);
  const [failedChecks, setFailedChecks] = useState<string[]>([]);
  // Show the toast only immediately after the failure is detected (not on re-load)
  const [showFailureToast, setShowFailureToast] = useState(false);
  // True while we're checking the journey state on mount (before running verifications)
  const [isCheckingState, setIsCheckingState] = useState(true);

  // Support both onAdvance (DynamicPage) and onDone (JourneyPage/legacy) prop names.
  const onDoneRef = useRef<(() => void) | undefined>(undefined);
  onDoneRef.current = onAdvance ?? onDone;
  // Guard: only call onDone once per mount, even in React strict-mode double-invoke
  const onDoneCalledRef = useRef(false);

  // ── Lock helper — calls backend and updates local state ──────────────────
  const lockAndBlock = async (failures: string[]) => {
    setFailedChecks(failures);
    setIsBlocked(true);
    setShowFailureToast(true);
    // Hide toast after 2.5 s
    setTimeout(() => setShowFailureToast(false), 2500);
    try {
      await axios.post(`${API_BASE_URL}${lockEndpoint}`, {
        failed_checks: failures,
      });
    } catch (err) {
      console.error("[NameMatch] Failed to persist verification block to backend:", err);
    }
  };

  // ── Check journey state on mount — restore blocked UI if already blocked ─
  useEffect(() => {
    if (isBuilderMode) {
      setIsCheckingState(false);
      return;
    }
    const checkState = async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/api/journey/state/`);
        const meta = res.data?.meta ?? {};
        if (meta[blockedMetaKey] === true) {
          const storedFailed: string[] = meta[failedChecksMetaKey] ?? [];
          setFailedChecks(storedFailed);
          setIsBlocked(true);
          // Do NOT show the toast on re-load — only show it on fresh failure
        }
      } catch (err) {
        console.warn("[NameMatch] Could not fetch journey state on mount:", err);
      } finally {
        setIsCheckingState(false);
      }
    };
    checkState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Run verifications ─────────────────────────────────────────────────────
  const runAllVerifications = async () => {
    try {
      const results: Array<{ label: string; status: string; message: string }> = [];

      for (let i = 0; i < activeEndpoints.length; i++) {
        setCurrentStep(i + 1);
        const ep = activeEndpoints[i];
        try {
          const res = await axios.post(`${API_BASE_URL}${ep.url}`, requestBody ?? {});
          const data = res.data ?? {};
          // Ruleset endpoints report their outcome as `verification_status`, the
          // per-check endpoints as `status`. Read BOTH — reading only `status`
          // made a failed ruleset look "pending", so the failure was ignored and
          // the page advanced anyway.
          results.push({
            label: ep.label,
            status: data.status ?? data.verification_status ?? "pending",
            message: data.message ?? "",
          });
        } catch (err: any) {
          // Individual verification HTTP errors (4xx) are non-fatal —
          // the backend records N/A or FAIL and we continue.
          // Only 5xx / network errors bubble up and trigger a retry.
          if (err?.response?.status && err.response.status < 500) {
            console.warn(`[NameMatch] ${ep.label} returned ${err.response.status} — continuing`);
            results.push({ label: ep.label, status: "failed", message: "" });
            continue;
          }
          throw err; // re-throw 5xx / network errors
        }
      }

      // Check if any verification explicitly failed
      const failures = results.filter(r => r.status === "failed");
      if (failures.length > 0) {
        console.warn(
          "[NameMatch] Verification(s) failed — blocking user:",
          failures.map(f => `${f.label}: ${f.message}`).join(", ")
        );
        await lockAndBlock(failures.map(f => f.label));
        return; // do NOT advance
      }

      // All gates passed. Fire the optional non-blocking side-effect write
      // BEFORE advancing, so it runs while this page is still mounted — but
      // never let it affect whether we advance. See `postRunEndpoint`.
      if (postRunEndpoint) {
        try {
          await axios.post(`${API_BASE_URL}${postRunEndpoint}`, {});
        } catch (err) {
          console.warn(
            `[NameMatch] non-blocking ${postRunEndpoint} failed — continuing:`,
            err,
          );
        }
      }

      // All done — advance the journey (guard against double-fire in React strict mode)
      if (onDoneRef.current && !onDoneCalledRef.current) {
        onDoneCalledRef.current = true;
        onDoneRef.current();
      }
    } catch (err) {
      console.error("[NameMatch] Verification suite failed, will retry in 30s:", err);
      setIsPending(true);
      setRetryIn(30);
    }
  };

  // Guard: prevent double-fire in React strict mode (which mounts → unmounts → remounts)
  const hasStartedRef = useRef(false);

  // Fire on mount (journey mode only) — but only AFTER the state check completes
  useEffect(() => {
    if (isBuilderMode) return;
    if (isCheckingState) return; // wait for state check
    if (isBlocked) return;       // already blocked — don't re-run
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      runAllVerifications();
    }
  }, [isCheckingState, isBlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown + auto-retry
  useEffect(() => {
    if (retryIn <= 0) {
      if (isPending) {
        setIsPending(false);
        setCurrentStep(0);
        runAllVerifications();
      }
      return;
    }
    const timer = setInterval(() => setRetryIn((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [retryIn, isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Builder palette preview ───────────────────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🔍</div>
        <p className="font-semibold text-gray-500">{verificationLabel}</p>
        <p className="text-xs mt-1">
          Auto-fires all identity verification checks on mount. Blocks permanently on failure.
        </p>
      </div>
    );
  }

  // ── Blocked UI ────────────────────────────────────────────────────────────
  if (isBlocked) {
    return (
      <BlockedView
        failedChecks={failedChecks}
        showToast={showFailureToast}
        title={blockedTitle}
        description={blockedDescription}
      />
    );
  }

  // ── Loading / checking state ──────────────────────────────────────────────
  const stepLabel =
    currentStep > 0 && currentStep <= activeEndpoints.length
      ? activeEndpoints[currentStep - 1].label
      : "Preparing…";

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center items-center p-4">
      <div className="flex flex-col items-center text-center max-w-sm">
        <div className="mb-8">
          <IntellendLogo size="md" />
        </div>

        <div className="relative mb-8">
          <div className="w-16 h-16 border-4 border-intellend-green/20 border-t-intellend-green rounded-full animate-spin" />
        </div>

        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          Verifying Your Identity
        </h1>
        <p className="text-gray-500 mb-4">
          Running name and identity checks. This usually takes a few seconds.
        </p>

        {currentStep > 0 && !isPending && (
          <div className="bg-slate-50 px-6 py-3 rounded-full border border-slate-100 flex items-center gap-3 mb-4">
            <span className="flex h-2 w-2 rounded-full bg-intellend-green animate-pulse" />
            <span className="text-sm font-medium text-slate-600">
              {currentStep}/{activeEndpoints.length} — {stepLabel}
            </span>
          </div>
        )}

        {retryIn > 0 && (
          <div className="bg-slate-50 px-6 py-3 rounded-full border border-slate-100 flex items-center gap-3">
            <span className="flex h-2 w-2 rounded-full bg-intellend-green animate-pulse" />
            <span className="text-sm font-medium text-slate-600">
              Retrying in {retryIn}s…
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NameMatchVerification;
